import { defaultGenerativeUILibrary } from "@assistant-ui/react-generative-ui";
import { z } from "zod";

import { Breakdown } from "./Breakdown";
import {
  DataTable,
  type DataTableColumn,
  type CellValue,
} from "./DataTable";
import { InfoCard } from "./InfoCard";
import { Notice } from "./Notice";
import { NumberTicker } from "./NumberTicker";
import { SpecSheet, type SpecRow } from "./SpecSheet";

const columnSchema = z.object({ label: z.string() });
const cellSchema = z.union([z.string(), z.number(), z.boolean(), z.null()]);

const captainLibrary = {
  ...defaultGenerativeUILibrary,

  Breakdown: {
    description: "Ranked category breakdown with horizontal proportion bars.",
    properties: z.object({
      title: z.string().optional(),
      items: z.array(z.object({ label: z.string(), count: z.number() })),
      total: z.number(),
      initialCount: z.number().optional(),
    }),
    render: (props: Record<string, unknown>) => (
      <Breakdown
        title={props.title as string | undefined}
        items={props.items as { label: string; count: number }[]}
        total={props.total as number}
        initialCount={props.initialCount as number | undefined}
      />
    ),
  },

  Table: {
    description: "Tabular data with columns and rows.",
    properties: z.object({
      columns: z.array(columnSchema),
      rows: z.array(z.array(cellSchema)),
      action: z
        .object({
          clientId: z.string().optional(),
          integration: z.string().optional(),
          module: z.string().optional(),
          label: z.string().optional(),
        })
        .optional(),
    }),
    render: (props: Record<string, unknown>) => (
      <DataTable
        columns={props.columns as DataTableColumn[]}
        rows={props.rows as CellValue[][]}
      />
    ),
  },

  SpecSheet: {
    description: "A labeled key-value sheet with optional emphasis rows.",
    properties: z.object({
      title: z.string(),
      subtitle: z.string().optional(),
      rows: z.array(
        z.object({
          label: z.string(),
          value: z.string(),
          emphasis: z.boolean().optional(),
        }),
      ),
      visibleCount: z.number(),
    }),
    render: (props: Record<string, unknown>) => (
      <SpecSheet
        title={String(props.title ?? "")}
        subtitle={props.subtitle as string | undefined}
        rows={props.rows as SpecRow[]}
        visibleCount={props.visibleCount as number}
      />
    ),
  },

  InfoCard: {
    description: "Rich card(s) with avatar, title, description, status badge, and tag pills.",
    properties: z.object({
      title: z.string().optional(),
      cards: z.array(
        z.object({
          title: z.string(),
          description: z.string().optional(),
          status: z.string().optional(),
          avatar: z.string().optional(),
          tags: z.array(z.string()).optional(),
          inactiveTags: z.array(z.string()).optional(),
        }),
      ),
    }),
    render: (props: Record<string, unknown>) => (
      <InfoCard
        title={props.title as string | undefined}
        cards={
          props.cards as {
            title: string;
            description?: string;
            status?: string;
            avatar?: string;
            tags?: string[];
            inactiveTags?: string[];
          }[]
        }
      />
    ),
  },

  Notice: {
    description: "A styled notice banner for errors, warnings, and informational messages.",
    properties: z.object({
      title: z.string().optional(),
      message: z.string(),
      variant: z.enum(["error", "warning", "info"]).optional(),
    }),
    render: (props: Record<string, unknown>) => (
      <Notice
        title={props.title as string | undefined}
        message={props.message as string}
        variant={props.variant as "error" | "warning" | "info" | undefined}
      />
    ),
  },

  NumberTicker: {
    description: "An animated number with a label.",
    properties: z.object({
      value: z.number(),
      label: z.string(),
    }),
    render: (props: Record<string, unknown>) => (
      <NumberTicker
        value={props.value as number}
        label={props.label as string}
      />
    ),
  },
};

export default captainLibrary;
